import { StringConstantsMixin } from '../../application/javascripts/react_components/mixins/shared/string_constants_mixin';
import { addAirQuality, addPollen, removeAirQuality, removePollen } from './overlays/breezometer';
import { addLightPollution, removeLightPollution } from './overlays/light_pollution';
import {
  addWaymarkedCycle,
  addWaymarkedMTB,
  addWaymarkedHiking,
  removeWaymarkedCycle,
  removeWaymarkedHiking,
  removeWaymarkedMTB
} from './overlays/waymarked_trails';
import { addWeather, removeWeather } from './overlays/weather';
import { addDistanceMarkers, removeDistanceMarkers } from './overlays/distance_markers';
import { addCommunityPhotos, removePhotos } from './overlays/photos';
import { addCommunityWaypoints, removeWaypoints } from './overlays/waypoints';
import { addPendingTrails, removePendingTrails } from './overlays/pending_trails';
import { addNearbyTrails, removeNearbyTrails } from './overlays/nearby_trails';
import { addMapsOrRecordings, removeMapsOrRecordings } from './overlays/maps_or_recordings';
import { atMapsToGeojson } from './sources/at_maps';
import { addTrailHeatmapOverlay, addResultsHeatmapOverlay, removeHeatmapOverlay } from './overlays/heatmap';
import { addContribs, removeContribs } from './overlays/contribs';
import { addAdminOverlay, removeAdminOverlay } from './overlays/admin_custom';
import { GREEN, RED } from '../constants/PinColor';

const strings = StringConstantsMixin.strings.OVERLAYS;

// Overlays are mapbox layer/source combinations that can be easily toggled on/off by the user
const overlayConfigs = {
  after: {
    displayName: 'Last 60 days',
    aclVisible: ['admin']
    // Note: add/remove set at bottom
  },
  airQuality: {
    displayName: strings.AIR_QUALITY,
    aclEnabled: ['pro'],
    add: map => addAirQuality(map),
    remove: map => removeAirQuality(map),
    terrainDisabled: true
  },
  clusterTrails: {
    displayName: 'Cluster trails',
    aclVisible: ['admin'],
    hasHeatmap: true,
    refreshOnPan: true,
    add: (map, { trailId, renderHoverCard, enabledOverlays }) =>
      addPendingTrails(map, trailId, renderHoverCard, 'clusterTrails', RED, enabledOverlays),
    remove: map => removePendingTrails(map, 'clusterTrails'),
    terrainDisabled: true
  },
  contribs: {
    displayName: 'Contrib VRs',
    aclVisible: ['admin'],
    aclEnabled: ['contributeTrail'],
    add: (map, { trailId }) => addContribs(map, trailId),
    remove: map => removeContribs(map),
    terrainDisabled: true
  },
  distanceMarkers: {
    displayName: strings.DISTANCE_MARKERS,
    aclEnabled: ['exploreMap'],
    add: (map, { exploreMap, plannerMap }) => addDistanceMarkers(map, atMapsToGeojson([plannerMap || exploreMap])),
    remove: map => removeDistanceMarkers(map),
    terrainDisabled: false
  },
  heatmap: {
    displayName: strings.HEATMAP,
    aclEnabled: ['pro'],
    filterable: true,
    add: (map, { trailId, results, enabledOverlays, renderHoverCard }) =>
      trailId
        ? addTrailHeatmapOverlay(map, 'default', trailId, enabledOverlays, renderHoverCard)
        : addResultsHeatmapOverlay(map, 'default', results, enabledOverlays, renderHoverCard),
    remove: map => removeHeatmapOverlay(map, 'default'),
    terrainDisabled: true,
    keyboardShortcut: 'P'
  },
  lightPollution: {
    displayName: strings.LIGHT,
    aclEnabled: ['pro'],
    add: map => addLightPollution(map),
    remove: map => removeLightPollution(map),
    terrainDisabled: true
  },
  limit: {
    displayName: 'Most Recent 50',
    aclVisible: ['admin'],
    terrainDisabled: true
    // Note: add/remove set at bottom
  },
  mapsAll: {
    displayName: 'Maps all',
    aclVisible: ['admin'],
    hasHeatmap: true,
    refreshOnPan: true,
    filterable: true,
    add: (map, { renderHoverCard, enabledOverlays }) => addMapsOrRecordings(map, renderHoverCard, 'mapsAll', enabledOverlays),
    remove: map => removeMapsOrRecordings(map, 'mapsAll'),
    terrainDisabled: true
  },
  nearbyTrails: {
    displayName: strings.NEARBY_TRAILS,
    aclEnabled: ['nearbyTrails'],
    hasHeatmap: true,
    refreshOnPan: true,
    add: (map, { trailId, renderHoverCard, enabledOverlays, adminUser }) => addNearbyTrails(map, trailId, renderHoverCard, enabledOverlays, adminUser),
    remove: map => removeNearbyTrails(map),
    terrainDisabled: false,
    keyboardShortcut: 'O'
  },
  pendingTrails: {
    displayName: 'Pending trails',
    aclVisible: ['admin'],
    hasHeatmap: true,
    refreshOnPan: true,
    add: (map, { trailId, renderHoverCard, enabledOverlays }) =>
      addPendingTrails(map, trailId, renderHoverCard, 'pendingTrails', GREEN, enabledOverlays),
    remove: map => removePendingTrails(map, 'pendingTrails')
  },
  photos: {
    displayName: strings.PHOTOS,
    aclEnabled: ['attachedToTrail', 'exploreMap'],
    add: (map, { exploreMap, profilePhoto, handlePhotoClick, adminUser }) =>
      addCommunityPhotos(map, 'community-photos', exploreMap, profilePhoto, handlePhotoClick, adminUser),
    remove: map => removePhotos(map, 'community-photos'),
    terrainDisabled: true
  },
  pollen: {
    displayName: strings.POLLEN,
    aclEnabled: ['pro'],
    add: map => addPollen(map),
    remove: map => removePollen(map),
    terrainDisabled: true
  },
  recordingsAll: {
    displayName: 'Rec. all',
    aclVisible: ['admin'],
    hasHeatmap: true,
    refreshOnPan: true,
    filterable: true,
    add: (map, { renderHoverCard, enabledOverlays, selectedFilterActivity, resultsDataOverrides }) =>
      addMapsOrRecordings(map, renderHoverCard, 'recordingsAll', enabledOverlays, selectedFilterActivity, resultsDataOverrides),
    remove: map => removeMapsOrRecordings(map, 'recordingsAll'),
    keyboardShortcut: 'L'
  },
  recordingsPop: {
    displayName: 'Rec. popular',
    aclVisible: ['admin'],
    hasHeatmap: true,
    refreshOnPan: true,
    filterable: true,
    add: (map, { renderHoverCard, enabledOverlays, selectedFilterActivity, resultsDataOverrides }) =>
      addMapsOrRecordings(map, renderHoverCard, 'recordingsPop', enabledOverlays, selectedFilterActivity, resultsDataOverrides),
    remove: map => removeMapsOrRecordings(map, 'recordingsPop'),
    keyboardShortcut: 'K'
  },
  adminOverlay: {
    displayName: 'Custom overlay',
    aclVisible: ['admin'],
    add: (map, { adminCustomizationSettings }) => addAdminOverlay(map, adminCustomizationSettings),
    remove: map => removeAdminOverlay(map),
    keyboardShortcut: 'Y'
  },
  waymarkedCycling: {
    displayName: strings.WAYMARKED_CYCLING,
    aclVisible: ['gpsies'],
    add: map => addWaymarkedCycle(map),
    remove: map => removeWaymarkedCycle(map),
    terrainDisabled: true
  },
  waymarkedHiking: {
    displayName: strings.WAYMARKED_HIKING,
    aclVisible: ['gpsies'],
    add: map => addWaymarkedHiking(map),
    remove: map => removeWaymarkedHiking(map),
    terrainDisabled: true
  },
  waymarkedMTB: {
    displayName: strings.WAYMARKED_MTB,
    aclVisible: ['gpsies'],
    add: map => addWaymarkedMTB(map),
    remove: map => removeWaymarkedMTB(map),
    terrainDisabled: true
  },
  waypoints: {
    displayName: strings.WAYPOINTS,
    aclEnabled: ['attachedToTrail', 'exploreMap'],
    add: (map, { exploreMap, renderWaypointPopup }) => addCommunityWaypoints(map, 'community-waypoints', exploreMap, renderWaypointPopup),
    remove: map => removeWaypoints(map, 'community-waypoints'),
    terrainDisabled: true
  },
  weather: {
    displayName: strings.WEATHER,
    aclEnabled: ['pro'],
    add: map => addWeather(map),
    remove: map => removeWeather(map),
    terrainDisabled: true
  }
};

// Init all admin heatmap overlay configs
// These heatmaps (unlike the normal heatmap overlay) are all based off of non-heatmap overlays
Object.entries(overlayConfigs).forEach(([key, config]) => {
  if (!config.hasHeatmap) {
    return;
  }
  const heatmapKey = `${key}Heatmap`;
  const heatmapConfig = {
    displayName: `${config.displayName} heatmap`,
    aclEnabled: [`${key}Enabled`], // only allow toggling if the related non-heatmap overlay is enabled
    aclVisible: ['admin'],
    add: config.add, // simply re-call add function of non-heatmap related layer
    remove: map => removeHeatmapOverlay(map, key) // remove just heatmap overlay
  };
  overlayConfigs[heatmapKey] = heatmapConfig;
});

// Refreshes all enabled overlays with a specific key
const refreshOverlays = (map, configParams, matchingKey) => {
  const { enabledOverlays } = configParams;
  enabledOverlays.forEach(key => {
    const config = overlayConfigs[key];
    if (!config[matchingKey]) {
      return;
    }
    config.add(map, configParams);
  });
};

// Refreshes all enabled heatmap overlays that can be filtered by 'after' or 'limit' params
const refreshFilterableOverlays = (map, configParams) => {
  refreshOverlays(map, configParams, 'filterable');
};
overlayConfigs.after.add = refreshFilterableOverlays;
overlayConfigs.after.remove = refreshFilterableOverlays;
overlayConfigs.limit.add = refreshFilterableOverlays;
overlayConfigs.limit.remove = refreshFilterableOverlays;

// Refreshes all enabled overlays that should be refreshed on panning the map
const refreshOverlaysOnPan = (map, configParams) => {
  refreshOverlays(map, configParams, 'refreshOnPan');
};

export { overlayConfigs, refreshOverlaysOnPan };
